import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from './LoadingSpinner';
import { TenantHeader } from './TenantHeader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** When true, skip tenant selection AND zero-membership checks (for /get-started, /join, /create-org, /select-company) */
  skipTenantCheck?: boolean;
  /** When true, only allow access for users with zero memberships (for /get-started page) */
  requireZeroMemberships?: boolean;
}

/**
 * ProtectedRoute component that guards routes requiring authentication.
 * Redirects unauthenticated users to login page while preserving the intended destination.
 * Redirects authenticated users without a selected tenant to the Company Selector.
 * Redirects zero-membership users to the Get Started page.
 * Displays TenantHeader on all authenticated pages when a tenant context is active.
 *
 * Requirements: 3.6, 4.1, 4.3, 4.4, 9.1, 9.3, 9.5
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  skipTenantCheck = false,
  requireZeroMemberships = false,
}) => {
  const { isAuthenticated, isLoading, requiresTenantSelection, activeTenant, pendingTenants } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page but save the location they were trying to access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Determine if user has zero memberships:
  // No active tenant, no pending tenants for selection, and tenant selection not required
  const hasZeroMemberships = !activeTenant && pendingTenants.length === 0 && !requiresTenantSelection;

  // Guard for /get-started: only accessible to zero-membership users
  if (requireZeroMemberships && !hasZeroMemberships) {
    return <Navigate to="/account" replace />;
  }

  if (!skipTenantCheck) {
    // Route zero-membership users to /get-started (Req 4.1, 4.3)
    if (hasZeroMemberships) {
      return <Navigate to="/get-started" replace />;
    }

    // Enforce tenant selection — redirect to Company Selector if tenant not yet chosen
    if (requiresTenantSelection) {
      return <Navigate to="/select-company" state={{ from: location }} replace />;
    }
  }

  return (
    <>
      {!skipTenantCheck && <TenantHeader />}
      {children}
    </>
  );
};
